import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function safeRandomUUID(): string {
  const c = (globalThis as any)?.crypto as Crypto | undefined
  const randomUUID = (c as any)?.randomUUID
  if (typeof randomUUID === "function") return randomUUID.call(c)

  const getRandomValues = c?.getRandomValues?.bind(c)
  if (typeof getRandomValues === "function") {
    const bytes = new Uint8Array(16)
    getRandomValues(bytes)
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"))
    return `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-${hex[4]}${hex[5]}-${hex[6]}${hex[7]}-${hex[8]}${hex[9]}-${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`
  }

  const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(1)
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`
}

export function haptic(pattern: number | number[] = 10) {
  try {
    if (typeof navigator === "undefined") return
    const vibrate = (navigator as any)?.vibrate
    if (typeof vibrate !== "function") return
    vibrate.call(navigator, pattern)
  } catch {
    void 0
  }
}

export function getInstitutionId(
  level: "gp" | "block" | "zila" | null | undefined,
  district?: string | null,
  block?: string | null,
  village?: string | null,
) {
  const normalizeKey = (v: string | null | undefined) =>
    (v ?? "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "")
      .replace(/-/g, "")

  const d = normalizeKey(district)
  const b = normalizeKey(block)
  const v = normalizeKey(village)

  if (level === "gp") {
    if (!d || !b || !v) return null
    const institutionId = `gp:${d}:${b}:${v}`
    return institutionId
  }

  if (level === "block") {
    if (!d || !b) return null
    const institutionId = `block:${d}:${b}`
    return institutionId
  }

  if (level === "zila") {
    if (!d) return null
    const institutionId = `zila:${d}`
    return institutionId
  }

  return null
}

export function getNormalizedInstitutionId(
  level: "gp" | "block" | "zila" | null | undefined,
  district?: string | null,
  block?: string | null,
  village?: string | null,
) {
  return getInstitutionId(level, district, block, village)
}

export function stripLangPrefix(pathname: string): string {
  if (pathname.startsWith("/en/") || pathname.startsWith("/hi/") || pathname.startsWith("/pa/")) return pathname.slice(3)
  if (pathname === "/en" || pathname === "/hi" || pathname === "/pa") return "/"
  return pathname
}

export function normalizeModuleSlug(slug: string | null | undefined): string | null {
  const raw = String(slug ?? "").trim().toLowerCase()
  if (!raw) return null
  if (raw === "online-services") return "services"
  if (raw === "my-village-weather") return "weather"
  return raw
}

export function getCanonicalModuleSlugFromPathname(pathname: string): string | null {
  const normalizedPath = stripLangPrefix(String(pathname || ""))

  const moduleNameByPrefix: Record<string, string> = {
    "/weather": "weather",
    "/my-village-weather": "weather",
    "/online-services": "services",
    "/farmer-services": "farmer-services",
    "/eminent-persons": "eminent-persons",
    "/my-music": "my-music",
    "/village-tour": "village-tour",
    "/village-hub": "village-hub",
    "/voter-election": "voter-election",
    "/samvaad": "samvaad",
    "/reelgram": "reelgram",
  }

  const matchedEntry = Object.entries(moduleNameByPrefix).find(
    ([prefix]) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`),
  )
  return matchedEntry?.[1] ?? null
}

export function getModulePageIdFromSlug(slug: string | null | undefined): string | null {
  const canonical = normalizeModuleSlug(slug)
  return canonical ? `module:${canonical}` : null
}

export function getHomeNavigationTarget(input: {
  pathname: string
  lang: string
  gpSearch?: string
}): { pathname: string; search: string; shouldClearInstitution: boolean } {
  const langPrefix = input.lang && input.lang !== "en" ? `/${input.lang}` : ""
  const homePath = langPrefix || "/"
  const gpPath = `${langPrefix}/gp`

  const normalizedPath = stripLangPrefix(String(input.pathname || "")).replace(/\/+$/, "")
  const segments = normalizedPath.split("/").filter(Boolean)

  const isInstitutionPage = segments[0] === "gp" && segments.length === 1
  const hasInstitutionSelected = Boolean(input.gpSearch)

  // If on institution page -> go to global home
  if (isInstitutionPage) {
    return { pathname: homePath, search: "", shouldClearInstitution: true }
  }

  // If on any other page and have institution -> go to institution page
  if (hasInstitutionSelected) {
    return { pathname: gpPath, search: input.gpSearch || "", shouldClearInstitution: false }
  }

  // Otherwise -> global home
  return { pathname: homePath, search: "", shouldClearInstitution: false }
}

export function toTitleCase(str: string): string {
  if (!str) return ""
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

/**
 * Hard lock: Prevents rendering raw location codes (e.g. "gp:123", "block:456") 
 * by validating against a code pattern.
 */
export function resolveDisplayName(name: string | null | undefined, code: string | null | undefined): string {
  const isCode = (v: string) => /^(gp:|block:|zila:|[0-9]{3,})/i.test(v)
  const n = String(name || "").trim()
  const c = String(code || "").trim()

  if (n && !isCode(n)) return n
  if (c && !isCode(c)) return toTitleCase(c)
  return ""
}
